import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Link, useNavigate } from 'react-router-dom';
import { useLearningData } from '../context/LearningDataContext';
import { BookIcon, LockIcon, CheckCircleIcon, ArrowRightIcon, ClockIcon, BarChart2Icon } from 'lucide-react';
export function LearningPath() {
  const {
    isArabic
  } = useLanguage();
  const {
    learningData
  } = useLearningData();
  const navigate = useNavigate();
  const handleUnitClick = (courseId: number, unitId: number) => {
    if (!courseId || !unitId) return;
    navigate(`/learn/course/${courseId}/unit/${unitId}`);
  };
  const handleCourseClick = (courseId: number) => {
    navigate(`/learn/course/${courseId}`);
  };
  // Add loading state check
  if (!learningData || !Array.isArray(learningData) || learningData.length === 0) {
    return <div className="w-full px-6 md:px-10 lg:px-20 py-16" data-id="element-729">
        <div className="max-w-4xl mx-auto text-center" data-id="element-730">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" data-id="element-731"></div>
          <p className="text-gray-600 dark:text-gray-300" data-id="element-732">
            {isArabic ? 'جارِ تحميل البيانات...' : 'Loading data...'}
          </p>
        </div>
      </div>;
  }
  return <div className="w-full px-6 md:px-10 lg:px-20 py-16" data-id="element-733">
      <div className="max-w-4xl mx-auto" data-id="element-734">
        <div className="text-center mb-12" data-id="element-735">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4" data-id="element-736">
            {isArabic ? 'مسار التعلم' : 'Learning Path'}
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300" data-id="element-737">
            {isArabic ? 'تتبع تقدمك وتعلم بخطوتك الخاصة' : 'Track your progress and learn at your own pace'}
          </p>
        </div>
        <div className="space-y-8" data-id="element-738">
          {learningData.map(course => <div key={course.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden" data-id="element-739">
              <div className="p-6" data-id="element-740">
                <div className="flex items-center justify-between mb-6" data-id="element-741">
                  <div className="flex items-center gap-4" data-id="element-742">
                    <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg" data-id="element-743">
                      <BookIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" data-id="element-744" />
                    </div>
                    <div data-id="element-745">
                      <h2 className="text-2xl font-semibold text-gray-900 dark:text-white" data-id="element-746">
                        {isArabic ? course.title.ar : course.title.en}
                      </h2>
                      <p className="text-gray-600 dark:text-gray-300 mt-1" data-id="element-747">
                        {isArabic ? course.description.ar : course.description.en}
                      </p>
                    </div>
                  </div>
                  <button onClick={() => handleCourseClick(course.id)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" data-id="element-748">
                    {isArabic ? 'ابدأ الدورة' : 'Start Course'}
                  </button>
                </div>
                <div className="flex items-center gap-6 mb-6" data-id="element-749">
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300" data-id="element-750">
                    <ClockIcon className="w-5 h-5" data-id="element-751" />
                    <span data-id="element-752">
                      {isArabic ? course.estimatedTimeAr : course.estimatedTime}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300" data-id="element-753">
                    <BarChart2Icon className="w-5 h-5" data-id="element-754" />
                    <span data-id="element-755">
                      {course.completedLessons}/{course.totalLessons}{' '}
                      {isArabic ? 'دروس مكتملة' : 'lessons completed'}
                    </span>
                  </div>
                </div>
                <div className="space-y-4" data-id="element-756">
                  {course.units && Array.isArray(course.units) && course.units.map(unit => <div key={unit.id} onClick={() => !unit.isLocked && handleUnitClick(course.id, unit.id)} className={`p-4 rounded-lg border transition-colors ${unit.isLocked ? 'border-gray-200 dark:border-gray-700 cursor-not-allowed' : 'border-blue-200 dark:border-blue-800 hover:bg-blue-50 dark:hover:bg-blue-900/10 cursor-pointer'}`} data-id="element-757">
                        <div className="flex items-center justify-between" data-id="element-758">
                          <div className="flex items-center gap-3" data-id="element-759">
                            {unit.isLocked ? <LockIcon className="w-5 h-5 text-gray-400" data-id="element-760" /> : unit.progress === 100 ? <CheckCircleIcon className="w-5 h-5 text-green-500" data-id="element-761" /> : <ArrowRightIcon className="w-5 h-5 text-blue-500" data-id="element-762" />}
                            <div data-id="element-763">
                              <h3 className="font-medium text-gray-900 dark:text-white" data-id="element-764">
                                {isArabic ? unit.title.ar : unit.title.en}
                              </h3>
                              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-765">
                                {isArabic ? unit.description.ar : unit.description.en}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4" data-id="element-766">
                            <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-767">
                              {unit.progress}%
                            </div>
                            <div className="w-24 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-768">
                              <div className="h-full bg-blue-600 dark:bg-blue-400" style={{
                        width: `${unit.progress}%`
                      }} data-id="element-769" />
                            </div>
                          </div>
                        </div>
                      </div>)}
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </div>;
}